import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  confirmFanSignUp,
  resendFanConfirmationCode,
} from '../auth/fan-auth';
import { useI18n } from '../i18n';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { AuthShell } from './auth/AuthShell';
import { friendlyFanAuthError } from './auth/errorMessages';
import {
  errorBox,
  infoBox,
  inputClass,
  labelClass,
  primaryButton,
  secondaryButton,
} from './auth/formStyles';

/**
 * Manual verification-code entry, for users who lost or never received
 * the email link. Separate from /confirm, which auto-confirms when
 * users click the link in the verification email.
 *
 * Pre-fills the email from `?email=` so the signup → here flow has
 * the address ready.
 */
export function ConfirmAccount() {
  const { t } = useI18n();
  useDocumentTitle(t('auth.confirm.title'));
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get('email') ?? '');
  const [code, setCode] = useState('');
  const [pending, setPending] = useState<'resend' | 'confirm' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function onResend(e: React.FormEvent) {
    e.preventDefault();
    if (!email) return;
    setPending('resend');
    setError(null);
    setInfo(null);
    try {
      await resendFanConfirmationCode(email.trim());
      setInfo(t('auth.confirm.resentInfo'));
    } catch (err) {
      setError(friendlyFanAuthError(err, t));
    } finally {
      setPending(null);
    }
  }

  async function onConfirm(e: React.FormEvent) {
    e.preventDefault();
    if (!email || !code) return;
    setPending('confirm');
    setError(null);
    setInfo(null);
    try {
      await confirmFanSignUp(email.trim(), code.trim());
      setDone(true);
    } catch (err) {
      setError(friendlyFanAuthError(err, t));
    } finally {
      setPending(null);
    }
  }

  if (done) {
    return (
      <AuthShell
        kicker={t('auth.confirm.kicker')}
        title={t('auth.confirm.doneTitle')}
        subtitle={t('auth.confirm.doneSubtitle')}
      >
        {/* Reuse the celebration burst rings from /confirm so the
            two verification paths (email-link landing vs manual-code
            entry) feel cohesive on success. */}
        <div className="flex justify-center">
          <div className="relative mb-3">
            <span aria-hidden="true" className="nx-confirm-ring nx-confirm-ring-1" />
            <span aria-hidden="true" className="nx-confirm-ring nx-confirm-ring-2" />
            <div className="relative h-16 w-16 rounded-full bg-blue-500/15 border border-blue-500/40 flex items-center justify-center text-blue-300 text-3xl shadow-[0_0_24px_rgba(59,130,246,0.5)]">
              ✓
            </div>
          </div>
        </div>
        <button type="button" onClick={() => nav('/login')} className={primaryButton}>
          {t('auth.confirm.toLogin')}
        </button>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      kicker={t('auth.confirm.kicker')}
      title={t('auth.confirm.title')}
      subtitle={t('auth.confirm.subtitle')}
      footer={
        <p className="text-center text-[11px] text-slate-500">
          <Link to="/login" className="underline hover:text-slate-300">
            {t('auth.confirm.backToLogin')}
          </Link>
        </p>
      }
      banner={info && !error ? <p className={infoBox}>{info}</p> : undefined}
    >
      <form onSubmit={onConfirm} className="space-y-3">
        <div>
          <label htmlFor="email" className={labelClass}>{t('auth.confirm.emailLabel')}</label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
            required
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor="code" className={labelClass}>{t('auth.confirm.codeLabel')}</label>
          <input
            id="code"
            type="text"
            inputMode="numeric"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder={t('auth.confirm.codePlaceholder')}
            autoComplete="one-time-code"
            required
            className={inputClass}
          />
        </div>
        <button type="submit" disabled={pending !== null} className={primaryButton}>
          {pending === 'confirm' ? t('auth.confirm.submitting') : t('auth.confirm.submit')}
        </button>
      </form>

      <div className="border-t border-slate-800 pt-4">
        <p className="mb-2 text-xs text-slate-400">{t('auth.confirm.noCode')}</p>
        <button
          type="button"
          onClick={onResend}
          disabled={pending !== null || !email}
          className={secondaryButton}
        >
          {pending === 'resend' ? t('auth.confirm.resending') : t('auth.confirm.resend')}
        </button>
      </div>

      {error && <p role="alert" className={errorBox}>{error}</p>}
    </AuthShell>
  );
}
